import { useMutation } from '@tanstack/react-query';
import { API_BASE_URL } from '../../config';

export interface BinaryUploadResponse {
  fileUrl: string;
  fileSize: number;
  sha512: string;
  release?: any; // Release object if created
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface BinaryUploadOptions {
  file: File;
  metadata?: {
    version?: string;
    channel?: string;
    platform?: string;
    architecture?: string;
    releaseNotes?: string;
    minimumAppVersion?: string;
    isMandatory?: boolean;
  };
}

export function useBinaryUpload(
  onProgress?: (progress: UploadProgress) => void
) {
  return useMutation<BinaryUploadResponse, Error, BinaryUploadOptions>({
    mutationFn: async ({ file, metadata }) => {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add metadata if provided
      if (metadata) {
        if (metadata.version) formData.append('version', metadata.version);
        if (metadata.channel) formData.append('channel', metadata.channel);
        if (metadata.platform) formData.append('platform', metadata.platform);
        if (metadata.architecture) formData.append('architecture', metadata.architecture);
        if (metadata.releaseNotes) formData.append('release_notes', metadata.releaseNotes);
        if (metadata.minimumAppVersion) formData.append('minimum_app_version', metadata.minimumAppVersion);
        if (metadata.isMandatory !== undefined) formData.append('is_mandatory', metadata.isMandatory.toString());
      }

      // Create XMLHttpRequest for progress tracking
      return new Promise<BinaryUploadResponse>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentage = Math.round((event.loaded / event.total) * 100);
              onProgress({
                loaded: event.loaded,
                total: event.total,
                percentage,
              });
            }
          });
        }

        // Handle completion
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.data) {
                resolve(response.data);
              } else {
                resolve(response);
              }
            } catch (error) {
              reject(new Error('Invalid response format'));
            }
          } else if (xhr.status === 413) {
            // File too large error
            reject(new Error('File too large. Files over 20MB require chunked upload. The upload will switch automatically.'));
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.message || errorData.error || `Upload failed with status ${xhr.status}`));
            } catch {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        });

        // Handle errors
        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        // Configure and send request
        xhr.open('POST', `${API_BASE_URL}/api/v1/binaries/upload`);
        xhr.withCredentials = true; // Include cookies
        xhr.send(formData);
      });
    },
  });
}